import React from "react";
import dayjs from "dayjs";
import ActivityIcon from "./ActivityIcon";
import { BATTERY_ACTIVITY_TYPES as ACTIVITY_TYPES } from "./constants";
import ActivityPill, { PILL_TYPES } from "./ActivityPill";

const renderActivityContent = (activity) => {
  const {
    type,
    hub,
    locker,
    agent,
    driver,
    trip,
    session,
    batteryCharge,
    severity,
    routeFrom,
    routeTo,
    isRental,
  } = activity;

  switch (type) {
    case ACTIVITY_TYPES.KEPT_FOR_CHARGING:
      return (
        <>
          <span>Kept for charging at</span>
          <ActivityPill>{hub}</ActivityPill>
          <span>Battery charge:</span>
          <ActivityPill percentage={batteryCharge} />
        </>
      );

    case ACTIVITY_TYPES.CHECKED_IN_SOS:
      return (
        <>
          <span>Checked-in by agent:</span>
          <ActivityPill>{agent}</ActivityPill>
          <span>for battery SOS request raised by driver:</span>
          <ActivityPill>{driver}</ActivityPill>
        </>
      );

    case ACTIVITY_TYPES.ASSIGNED_SOS:
      return (
        <>
          <span>Assigned to battery SOS request raised by driver:</span>
          <ActivityPill>{driver}</ActivityPill>
        </>
      );

    case ACTIVITY_TYPES.WITHDRAWN_LOCKER:
      return (
        <>
          <span>Withdrawn from locker:</span>
          <ActivityPill>{locker}</ActivityPill>
          <span>at</span>
          <ActivityPill>{hub}</ActivityPill>
          {agent && (
            <>
              <span>by agent:</span>
              <ActivityPill>{agent}</ActivityPill>
            </>
          )}
          {trip && (
            <>
              <span>
                {isRental ? "and associated to rental" : "and associated to"}
              </span>
              <ActivityPill>{trip}</ActivityPill>
            </>
          )}
        </>
      );

    case ACTIVITY_TYPES.DEPOSITED_LOCKER:
      return (
        <>
          <span>Deposited in locker</span>
          <ActivityPill>{locker}</ActivityPill>
          <span>at</span>
          <ActivityPill>{hub}</ActivityPill>
          {agent && (
            <>
              <span>by agent:</span>
              <ActivityPill>{agent}</ActivityPill>
            </>
          )}
        </>
      );

    case ACTIVITY_TYPES.ASSIGNED_TRIP:
      return (
        <>
          <span>Assigned to</span>
          <ActivityPill>{trip}</ActivityPill>
          <span>with driver:</span>
          <ActivityPill>{driver}</ActivityPill>
        </>
      );

    case ACTIVITY_TYPES.REMOVED_FROM_TRIP:
      return (
        <>
          <span>Removed from</span>
          <ActivityPill>{trip}</ActivityPill>
          {driver && (
            <>
              <span>with driver:</span>
              <ActivityPill>{driver}</ActivityPill>
            </>
          )}
        </>
      );

    case ACTIVITY_TYPES.FULLY_CHARGED:
      return (
        <>
          <span>Battery fully charged at</span>
          <ActivityPill>{hub}</ActivityPill>
          <span>Battery charge:</span>
          <ActivityPill percentage={batteryCharge} />
        </>
      );

    case ACTIVITY_TYPES.CHECKED_IN:
      return (
        <>
          <span>Checked-in at</span>
          <ActivityPill>{hub}</ActivityPill>
          {driver && (
            <>
              <span>by driver:</span>
              <ActivityPill>{driver}</ActivityPill>
            </>
          )}
          {agent && (
            <>
              <span>by agent:</span>
              <ActivityPill>{agent}</ActivityPill>
            </>
          )}
          {trip && (
            <>
              <span>for</span>
              <ActivityPill>{trip}</ActivityPill>
            </>
          )}
        </>
      );

    case ACTIVITY_TYPES.CHECKED_OUT:
      return (
        <>
          <span>Checked-out from</span>
          <ActivityPill>{hub}</ActivityPill>
          {agent && (
            <>
              <span>by agent:</span>
              <ActivityPill>{agent}</ActivityPill>
            </>
          )}
          {driver && (
            <>
              <span>by driver:</span>
              <ActivityPill>{driver}</ActivityPill>
            </>
          )}
          {trip && (
            <>
              <span>for</span>
              <ActivityPill>{trip}</ActivityPill>
            </>
          )}
        </>
      );

    case ACTIVITY_TYPES.ASSIGNED_SWAP:
      return (
        <>
          <span>Assigned for swap from</span>
          <ActivityPill>{hub}</ActivityPill>
          {trip && (
            <>
              <span>{isRental ? "for rental" : "for"}</span>
              <ActivityPill>{trip}</ActivityPill>
            </>
          )}
        </>
      );

    case ACTIVITY_TYPES.ASSIGNED_RENTAL:
      return (
        <>
          <span>Assigned to rental</span>
          <ActivityPill>{trip}</ActivityPill>
          <span>with driver:</span>
          <ActivityPill>{driver}</ActivityPill>
        </>
      );

    case ACTIVITY_TYPES.ASSOCIATED_RENTAL:
      return (
        <>
          <span>Associated to active rental</span>
          <ActivityPill>{trip}</ActivityPill>
        </>
      );

    case ACTIVITY_TYPES.ASSIGNED_TRANSFER:
      return (
        <>
          <span>Assigned for transfer from</span>
          <ActivityPill from={routeFrom} to={routeTo} />
          {trip && (
            <>
              <span>for</span>
              <ActivityPill>{trip}</ActivityPill>
            </>
          )}
        </>
      );

    case ACTIVITY_TYPES.ASSIGNED_SESSION:
      return (
        <>
          <span>Assigned to</span>
          <ActivityPill>{session}</ActivityPill>
          <span>with driver:</span>
          <ActivityPill>{driver}</ActivityPill>
        </>
      );

    case ACTIVITY_TYPES.ASSOCIATED_SESSION:
      return (
        <>
          <span>Associated with active</span>
          <ActivityPill>{session}</ActivityPill>
        </>
      );

    case ACTIVITY_TYPES.REMOVED_FROM_SESSION:
      return (
        <>
          <span>Removed from</span>
          <ActivityPill>{session}</ActivityPill>
          {driver && (
            <>
              <span>with driver:</span>
              <ActivityPill>{driver}</ActivityPill>
            </>
          )}
        </>
      );

    case ACTIVITY_TYPES.ADDED_TO_SERVICE:
      return (
        <>
          <span>Added to service at:</span>
          <ActivityPill>{hub}</ActivityPill>
          <span>with severity level:</span>
          <ActivityPill type={PILL_TYPES.SEVERITY}>{severity}</ActivityPill>
        </>
      );

    case ACTIVITY_TYPES.SERVICE_COMPLETED:
      return <span>Service completed</span>;

    default:
      return <span>Activity recorded</span>;
  }
};

const ActivityTimelineItem = ({
  activity,
  isFirst = false,
  // isLast = false,
}) => {
  return (
    <div className="flex items-center w-full">
      {/* Time column */}
      <div className="w-[72px] text-left font-poppins font-medium text-xs text-[#525252] flex-shrink-0">
        {dayjs(activity.createdAt).format("h:mm a")}
      </div>

      {/* Timeline spine + Icon */}
      <div className="relative flex flex-col items-center flex-shrink-0 w-7">
        {!isFirst && (
          <div className="absolute top-[-32px] h-8 w-[1px] bg-light-grey left-1/2 -translate-x-1/2" />
        )}
        {/* {!isLast && (
          <div className="absolute top-3.5 bottom-0 w-[1px] bg-light-grey left-1/2 -translate-x-1/2" />
        )} */}
        <ActivityIcon
          type={activity.type}
          className="relative z-10 w-6 h-6 flex-shrink-0"
        />
      </div>

      {/* Description & Pills */}
      <div className="flex-1 font-poppins font-medium text-xs text-[#525252] flex flex-wrap items-center gap-x-1.5 gap-y-1.5 pl-3">
        {renderActivityContent(activity)}
      </div>
    </div>
  );
};

export default ActivityTimelineItem;
